// Copyright ©️ 2026 eVolve MEP, LLC

import { Box, Button, Divider, Group, Stack, Text, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNil, isNotNil } from 'helpers/isNotNil';

import { useOptionalBendCreatorCartContext } from '../Context/useBendCreatorCartContext';
import { BendCreatorStepFields } from './BendCreatorStepFields';
import { NavArrowActionIcon } from './NavArrowActionIcon';

export const bendCreatorSteps = ['Material', 'Bend Type', 'Dimensions', 'Review'] as const;
export type BendCreatorStep = (typeof bendCreatorSteps)[number];

export const BendCreatorStepPanel = ({
  stepName,
  stepIndex,
  onChangeStep,
}: {
  stepName: BendCreatorStep;
  stepIndex: number;
  onChangeStep: (step: BendCreatorStep | null) => void;
}) => {
  const cartContext = useOptionalBendCreatorCartContext();
  const isEditingBendFromCart = isNotNil(cartContext?.selectedItem);
  const nextStep = bendCreatorSteps[stepIndex + 1] ?? null;
  return (
    <>
      <Group p="sm">
        <AgGridStyleTooltip label="Go back to the list of steps">
          <NavArrowActionIcon onClick={() => onChangeStep(null)} direction="left" />
        </AgGridStyleTooltip>
        <Box>
          <Text c="dimmed" fz="xs">
            Step {stepIndex + 1} of {bendCreatorSteps.length}
          </Text>
          <Title order={4}>{stepName}</Title>
        </Box>
      </Group>
      <Divider />
      <Stack p="sm">
        <BendCreatorStepFields stepName={stepName} onChangeStep={onChangeStep} />
      </Stack>
      <Divider />
      <Box display="flex" p="sm">
        {isNil(nextStep) || isEditingBendFromCart ? (
          <Button flex={1} type="submit" variant={isEditingBendFromCart ? 'outline' : 'filled'}>
            {isEditingBendFromCart ? 'Done' : 'Add to bend list'}
          </Button>
        ) : (
          <Button
            flex={1}
            onClick={() => onChangeStep(nextStep)}
            rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
            size="lg"
          >
            Next: {nextStep}
          </Button>
        )}
      </Box>
    </>
  );
};
